/**
 * @description Games 列构建器（Top Games / Rising Games）
 */
import React from 'react';
import { getIntl } from 'umi';
import Bubble from '@home/components/TeaComponents/Bubble';
import ColumnAligner from '@intelligence/v3/components/TableComponents/ColumnAligner';
import GamesListCell from './GamesListCell';
import { parseJSONString } from '@intelligence/v3/utils/common';

import type { TableColumn } from 'tea-component';
import type { TMetricType } from '../../../types';

/**
 * 游戏信息
 */
interface IGameInfo {
  entity_name?: string;
  cover?: string;
}

/**
 * Games 列配置
 */
interface IGamesColumnConfig {
  /** 列key */
  key: string;
  /** 列标题 i18n key */
  headerKey: string;
  /** 提示文案 i18n key */
  tooltipKey?: string;
  /** 列宽度 */
  width?: number;
  /** 取值字段 */
  field: keyof ICasualGenreProfileMetricData;
}

/**
 * Games 列渲染器配置
 */
interface IGamesRenderConfig {
  /** 实体类型 */
  entityType: IEntityType;
  /** 游戏信息Map */
  entityInfoMap: IRecord<IGameInfo>;
  /** More按钮点击回调 */
  onMoreClick?: (columnKey: string, record: ICasualGenreProfileMetricData) => void;
}

/**
 * Top Games 列配置
 */
const TOP_GAMES_COLUMN_CONFIGS: Record<TMetricType, IGamesColumnConfig> = {
  download: {
    key: 'topGamesByDownloads',
    headerKey: 'dbi_casual_table_col_top_games_by_download',
    width: 250,
    field: 'top_download_games',
  },
  dau: {
    key: 'topGamesByDAU',
    headerKey: 'dbi_casual_table_col_top_games_by_dau',
    width: 250,
    field: 'top_avg_dau_games',
  },
  wau: {
    key: 'topGamesByWAU',
    headerKey: 'dbi_casual_table_col_top_games_by_wau',
    width: 250,
    field: 'top_avg_wau_games',
  },
  mau: {
    key: 'topGamesByMAU',
    headerKey: 'dbi_casual_table_col_top_games_by_mau',
    width: 250,
    field: 'top_avg_mau_games',
  },
  revenue: {
    key: 'topGamesByRevenue',
    headerKey: 'dbi_casual_table_col_top_games_by_revenue',
    width: 250,
    field: 'top_revenue_games',
  },
};

/**
 * Rising Games 列配置（带tooltip key）
 */
const RISING_GAMES_COLUMN_CONFIGS: Record<TMetricType, IGamesColumnConfig> = {
  download: {
    key: 'risingGamesByDownloads',
    headerKey: 'dbi_casual_table_col_rising_games_by_download',
    tooltipKey: '', // 动态计算
    width: 250,
    field: 'rising_download_games',
  },
  dau: {
    key: 'risingGamesByDAU',
    headerKey: 'dbi_casual_table_col_rising_games_by_dau',
    tooltipKey: '', // 动态计算
    width: 250,
    field: 'rising_avg_dau_games',
  },
  wau: {
    key: 'risingGamesByWAU',
    headerKey: 'dbi_casual_table_col_rising_games_by_wau',
    tooltipKey: '',
    width: 250,
    field: 'rising_avg_wau_games',
  },
  mau: {
    key: 'risingGamesByMAU',
    headerKey: 'dbi_casual_table_col_rising_games_by_mau',
    tooltipKey: '', // 动态计算
    width: 250,
    field: 'rising_avg_mau_games',
  },
  revenue: {
    key: 'risingGamesByRevenue',
    headerKey: 'dbi_casual_table_col_rising_games_by_revenue',
    tooltipKey: '',
    width: 250,
    field: 'rising_revenue_games',
  },
};

const getValidGameIds = (list?: string[] | string): string[] => {
  if (Array.isArray(list)) {
    return list;
  }

  if (typeof list === 'string') {
    return parseJSONString([], list);
  }

  return [];
};

/**
 * 构建 Games 列
 * @param config 列配置
 * @param renderConfig 渲染配置
 * @returns 表格列配置
 */
const buildGamesColumn = (
  config: IGamesColumnConfig,
  renderConfig?: IGamesRenderConfig,
): TableColumn<ICasualGenreProfileMetricData> => {
  const { key, headerKey, tooltipKey, width = 250, field } = config;
  const { formatMessage } = getIntl();
  const header = formatMessage({ id: headerKey });
  const tooltipText = tooltipKey ? formatMessage({ id: tooltipKey }) : '';

  return {
    key,
    header: (
      <ColumnAligner align="center" width={width}>
        {tooltipKey ? (
          <Bubble className="dbi-bubble-max-width" dark arrowPointAtCenter content={tooltipText}>
            <span style={{ cursor: 'pointer' }}>{header}</span>
          </Bubble>
        ) : (
          <span>{header}</span>
        )}
      </ColumnAligner>
    ),
    width,
    align: 'center',
    render: (record) => {
      const gameIds = getValidGameIds(record?.[field] as string[]);
      const entityType = renderConfig?.entityType || 'mobile';
      const entityInfoMap = renderConfig?.entityInfoMap || {};
      const onMoreClick = renderConfig?.onMoreClick;

      return (
        <ColumnAligner align="center" width={width}>
          <GamesListCell
            entityType={entityType}
            gameIds={gameIds}
            entityInfoMap={entityInfoMap}
            onMoreClick={() => onMoreClick?.(key, record)}
          />
        </ColumnAligner>
      );
    },
  };
};

/**
 * 根据指标和表格Segment获取 Top Games 列
 * @param granularity 当前日期粒度
 * @param renderConfig 渲染配置
 * @returns Top Games 列配置数组（同时返回 Downloads 和 MAU/DAU 两列）
 */
export const getTopGamesColumns = (
  granularity: IGranularity,
  renderConfig?: IGamesRenderConfig,
): TableColumn<ICasualGenreProfileMetricData>[] => {
  // 根据粒度展示 Downloads 和 MAU/DAU 两列
  const downloadConfig = TOP_GAMES_COLUMN_CONFIGS.download;
  const auMetric = granularity === 'daily' ? 'dau' : 'mau';
  const auConfig = TOP_GAMES_COLUMN_CONFIGS[auMetric];
  return [buildGamesColumn(downloadConfig, renderConfig), buildGamesColumn(auConfig, renderConfig)];
};

/**
 * 根据指标和表格Segment获取 Rising Games 列
 * @param granularity 当前日期粒度
 * @param renderConfig 渲染配置
 * @returns Rising Games 列配置数组（同时返回 Downloads 和 MAU/DAU 两列）
 */
export const getRisingGamesColumns = (
  granularity: IGranularity,
  renderConfig?: IGamesRenderConfig,
): TableColumn<ICasualGenreProfileMetricData>[] => {
  const isDaily = granularity === 'daily';

  // Downloads列的tooltip
  const downloadTooltipKey = isDaily
    ? 'dbi_casual_rising_games_col_tip_daily_download'
    : 'dbi_casual_rising_games_col_tip_monthly_download';

  // AU列的tooltip (DAU或MAU)
  const auTooltipKey = isDaily ? 'dbi_casual_rising_games_col_tip_dau' : 'dbi_casual_rising_games_col_tip_mau';

  // 根据粒度展示 Downloads 和 MAU/DAU 两列
  const downloadConfig = { ...RISING_GAMES_COLUMN_CONFIGS.download, tooltipKey: downloadTooltipKey };
  const auMetric = granularity === 'daily' ? 'dau' : 'mau';
  const auConfig = { ...RISING_GAMES_COLUMN_CONFIGS[auMetric], tooltipKey: auTooltipKey };

  return [buildGamesColumn(downloadConfig, renderConfig), buildGamesColumn(auConfig, renderConfig)];
};
